import { useQuery } from '@tanstack/react-query'
import { Brain, CalendarCheck, ChartLineDown, ClockCounterClockwise, ShieldCheck } from '@phosphor-icons/react'
import { apiRequest, errorMessage } from '../api/client'
import type { WeeklyReview, WeeklyReviewDecision } from '../api/types'

const actionLabels: Record<WeeklyReviewDecision['action'], string> = {
  NoChange: 'Plán zůstává beze změny',
  DeferDeload: 'Deload je bezpečně odložen',
  Deload: 'Navržený týden s nižším objemem',
  ReturnOverlay: 'Pozvolný návrat po pauze',
  SecondaryAdjustment: 'Odlehčení vedlejší expozice',
  StagnationExperiment: 'Kontrolovaný pokus proti stagnaci',
}

const actionDescriptions: Record<WeeklyReviewDecision['action'], string> = {
  NoChange: 'Dostupná data neukazují důvod měnit současný rozpis.',
  DeferDeload: 'Výkon stále roste. Odklad je omezený a při dalším review se znovu ověří.',
  Deload: 'Primárně se sníží počet sérií. Výsledky deloadu neposouvají běžný progresní streak.',
  ReturnOverlay: 'Jednorázově se sníží náročnost nejbližších expozic. Aktivní plán se trvale neregresuje.',
  SecondaryAdjustment: 'Lehčí expozice se upraví tak, aby nebrzdila hlavní cíl ani další prioritní den.',
  StagnationExperiment: 'Nejdřív se mění existující prescription. Nový doplněk lze navrhnout jen při volné kapacitě.',
}

const hypothesisLabels: Record<WeeklyReviewDecision['hypothesis'], string> = {
  None: 'Bez pracovní hypotézy',
  Capacity: 'Kapacita',
  Technique: 'Technika',
  FatigueInterference: 'Kumulovaná zátěž a interference',
  Mobility: 'Mobilita',
  Stability: 'Stabilita',
  Grip: 'Úchop',
  Asymmetry: 'Asymetrie',
  Adherence: 'Pravidelnost',
}

const triggerLabels: Record<WeeklyReview['trigger'], string> = {
  EndOfWeek: 'Konec týdne',
  BeforeNewWeek: 'Před novým týdnem',
  PlanRevision: 'Po změně plánu',
  ReturnAfterPause: 'Návrat po pauze',
  PainOrFatigue: 'Bezpečnostní signál',
}

const statusLabels: Record<WeeklyReview['status'], string> = {
  Pending: 'Čeká na vyhodnocení',
  Processing: 'Vyhodnocuje se',
  Completed: 'Vyhodnoceno',
  Failed: 'Vyhodnocení se nezdařilo',
  Superseded: 'Nahrazeno novější kontrolou',
}

const missedOptionLabels: Record<WeeklyReviewDecision['missedWorkoutOptions'][number], string> = {
  Skip: 'Vynechat bez dohánění',
  Reschedule: 'Přesunout v rámci kapacity',
  MinimumDose: 'Zvolit minimální dávku',
}

const reasonLabels: Record<string, string> = {
  WEEKLY_EVIDENCE_STABLE: 'Týdenní evidence je stabilní',
  MISSED_WORKOUT_REQUIRES_USER_CHOICE: 'Zmeškaný den se neslučuje automaticky',
  SIX_WEEK_WINDOW_PROGRESSING: 'V šestitýdenním okně je stále měřitelný růst',
  DELOAD_DEFERRED_ONE_WEEK: 'Deload je odložen pouze o jeden týden',
  DELOAD_DEFERRAL_LIMIT_REACHED: 'Limit odkladů deloadu byl dosažen',
  SIX_WEEK_WINDOW_NO_VALID_PROGRESS: 'Šest týdnů bez dostatečně podloženého posunu',
  EARLY_DELOAD_ACCUMULATED_FAILURE: 'Nahromaděná selhání podporují dřívější deload',
  VOLUME_FIRST: 'Nejdřív se snižuje objem, ne obtížnost hlavního cíle',
  RETURN_AFTER_14_DAY_PAUSE: 'Od posledního dokončeného tréninku uplynulo alespoň 14 dní',
  ONE_SHOT_OVERLAY_ONLY: 'Změna platí jen pro nejbližší odpovídající expozici',
  SECONDARY_INTERFERENCE_PATTERN: 'Vedlejší expozice opakovaně zasahuje do hlavní',
  PRESERVE_PRIMARY_INTENT: 'Hlavní cíl a jeho prescription zůstávají chráněné',
  THREE_COMPARABLE_WEEKS_NO_PROGRESS: 'Tři srovnatelné týdny bez validního posunu',
  ADJUST_EXISTING_PRESCRIPTION_FIRST: 'Nejdřív se upraví stávající práce, nepřidává se slepě další cvik',
}

const dateFormatter = new Intl.DateTimeFormat('cs-CZ', { day: 'numeric', month: 'short', year: 'numeric' })

function formatWeek(value: string): string {
  return dateFormatter.format(new Date(`${value}T12:00:00`))
}

function ReviewBody({ review, rolloutMode }: { review: WeeklyReview; rolloutMode: string }) {
  const decision = review.decision
  if (!decision) {
    return (
      <div className="weekly-review-card__pending" aria-live="polite">
        <ClockCounterClockwise size={21} />
        <p>{statusLabels[review.status]}. Trénink můžeš dál normálně používat.</p>
      </div>
    )
  }

  return (
    <>
      <div className="weekly-review-card__summary">
        <div className="weekly-review-card__icon">
          {decision.action === 'Deload' || decision.action === 'ReturnOverlay'
            ? <ChartLineDown size={22} weight="duotone" />
            : <Brain size={22} weight="duotone" />}
        </div>
        <div>
          <h3>{actionLabels[decision.action]}</h3>
          <p>{actionDescriptions[decision.action]}</p>
        </div>
      </div>

      <dl className="weekly-review-facts">
        <div><dt>Plán zkontrolován</dt><dd>{decision.includedPlanItemIds.length} položek</dd></div>
        <div><dt>Přímo dotčeno</dt><dd>{decision.affectedPlanItemIds.length} položek</dd></div>
        <div><dt>Objem návrhu</dt><dd>{Math.round(decision.volumeMultiplier * 100)} %</dd></div>
        <div><dt>Pracovní hypotéza</dt><dd>{hypothesisLabels[decision.hypothesis]}</dd></div>
      </dl>

      <ul className="weekly-review-reasons" aria-label="Důvody týdenního vyhodnocení">
        {decision.reasonCodes.map((reason) => <li key={reason}>{reasonLabels[reason] ?? reason}</li>)}
      </ul>

      {decision.missedWorkoutOptions.length > 0 && (
        <div className="weekly-review-options">
          <strong>Zmeškaný den se nedohání automaticky</strong>
          <p>Server po kontrole zbývající kapacity povolil tyto bezpečné možnosti:</p>
          <div>{decision.missedWorkoutOptions.map((option) => <span key={option}>{missedOptionLabels[option]}</span>)}</div>
        </div>
      )}

      {decision.mayAddAccessory && (
        <p className="weekly-review-note">Volná kapacita dovoluje nejvýše jeden dočasný doplněk, ale až po potvrzení konkrétního návrhu.</p>
      )}

      <div className="weekly-review-guardrail">
        <ShieldCheck size={18} weight="duotone" />
        <span>{rolloutMode === 'Shadow'
          ? 'Stínový režim: výsledek je uložený pro kontrolu, ale prescription se automaticky nezměnila.'
          : 'Každá změna prescription se použije jen přes serverový návrh a tvoje potvrzení.'}</span>
      </div>
    </>
  )
}

export function WeeklyReviewPanel({ planActivationId, rolloutMode }: { planActivationId: string; rolloutMode: string }) {
  const reviews = useQuery({
    queryKey: ['weekly-reviews', planActivationId],
    queryFn: () => apiRequest<WeeklyReview[]>(`/coach/weekly-reviews?planActivationId=${encodeURIComponent(planActivationId)}&limit=6`),
  })

  return (
    <section className="content-section weekly-review-section" aria-labelledby="weekly-review-heading">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Celý plán, všechny dny</p>
          <h2 id="weekly-review-heading">Týdenní kontrola coache</h2>
          <p>Review porovnává každý aktivní cvik, cíle, pořadí, zátěž i předchozí týdny.</p>
        </div>
        <CalendarCheck size={25} weight="duotone" />
      </div>

      {reviews.isPending && <div className="weekly-review-loading" aria-live="polite">Načítám poslední vyhodnocení…</div>}
      {reviews.isError && (
        <div className="weekly-review-loading" role="alert">
          <span>{errorMessage(reviews.error)}</span>
          <button type="button" className="button button--quiet" onClick={() => reviews.refetch()}>Zkusit znovu</button>
        </div>
      )}
      {reviews.data?.length === 0 && (
        <div className="weekly-review-empty">
          <strong>První týdenní review ještě nevzniklo</strong>
          <p>Vytvoří se po poslední expozici týdne, před novým týdnem, po změně plánu, delší pauze nebo bezpečnostním signálu.</p>
        </div>
      )}
      {reviews.data && reviews.data.length > 0 && (
        <div className="weekly-review-list">
          {reviews.data.map((review, index) => (
            <article className={`weekly-review-card ${index === 0 ? 'weekly-review-card--latest' : ''}`} key={review.id}>
              <header>
                <div><span>{index === 0 ? 'Poslední kontrola' : 'Historie'}</span><strong>Týden od {formatWeek(review.weekStart)}</strong></div>
                <div className="weekly-review-card__status"><span>{triggerLabels[review.trigger]}</span><b>{statusLabels[review.status]}</b></div>
              </header>
              <ReviewBody review={review} rolloutMode={rolloutMode} />
            </article>
          ))}
        </div>
      )}
    </section>
  )
}
